import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Tag, Clock, Share2, Copy, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { publicApi } from '../lib/api';
import { toast } from 'sonner';

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    const loadPost = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await publicApi.getBlogPost(slug);
        setPost(res.data);
        
        // Load related posts from same category
        const allPosts = await publicApi.getBlogPosts();
        const related = allPosts.data
          .filter(p => p.category === res.data.category && p.slug !== slug)
          .slice(0, 3);
        setRelatedPosts(related);
        
        // Track page view
        publicApi.trackEvent({ event_type: 'page_view', page: `blog/${slug}` }).catch(() => {});
      } catch (err) {
        console.error('Error loading post:', err);
        if (err.response?.status === 404) {
          setError('Article not found');
        } else {
          setError('Failed to load article');
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (slug) {
      loadPost();
      window.scrollTo(0, 0);
    }
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const estimateReadTime = (content) => {
    const wordsPerMinute = 200;
    const wordCount = content?.split(/\s+/).length || 0;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return `${minutes} min read`;
  };

  const handleShare = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: url,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(url);
        }
      }
    } else {
      copyToClipboard(url);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Simple markdown-like rendering for content
  const renderContent = (content) => {
    if (!content) return null;
    
    // Split by double newlines for paragraphs
    const paragraphs = content.split(/\n\n+/);
    
    return paragraphs.map((paragraph, index) => {
      // Check for headers
      if (paragraph.startsWith('## ')) {
        return (
          <h2 key={index} className="text-2xl font-serif text-[#222222] mt-10 mb-4">
            {paragraph.replace('## ', '')}
          </h2>
        );
      }
      if (paragraph.startsWith('### ')) {
        return (
          <h3 key={index} className="text-xl font-serif text-[#222222] mt-8 mb-3">
            {paragraph.replace('### ', '')}
          </h3>
        );
      }
      
      // Check for bullet lists
      if (paragraph.includes('\n- ')) {
        const lines = paragraph.split('\n');
        return (
          <ul key={index} className="list-disc pl-6 space-y-2 my-4">
            {lines.map((line, i) => {
              if (line.startsWith('- ')) {
                return <li key={i} className="text-[#555555]">{line.replace('- ', '')}</li>;
              }
              return <p key={i} className="text-[#555555] mb-2">{line}</p>;
            })}
          </ul>
        );
      }
      
      // Regular paragraph
      return (
        <p key={index} className="text-[#555555] leading-relaxed mb-4">
          {paragraph}
        </p>
      );
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCFA]" data-testid="blog-post-loading">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
          <div className="animate-pulse space-y-6">
            <div className="h-4 bg-[#E8E4DC] w-24"></div>
            <div className="h-12 bg-[#E8E4DC] w-3/4"></div>
            <div className="h-4 bg-[#E8E4DC] w-48"></div>
            <div className="space-y-4 mt-8">
              <div className="h-4 bg-[#E8E4DC] w-full"></div>
              <div className="h-4 bg-[#E8E4DC] w-full"></div>
              <div className="h-4 bg-[#E8E4DC] w-2/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFCFA] flex items-center justify-center" data-testid="blog-post-error">
        <div className="text-center px-6">
          <h1 className="text-4xl font-serif text-[#222222] mb-4">{error}</h1>
          <p className="text-[#555555] mb-8">The article you're looking for may have been moved or deleted.</p>
          <Link to="/resources">
            <Button className="bg-[#1F2A44] hover:bg-[#151D30] text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Resources
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-[#FDFCFA]" data-testid="blog-post-page">
      {/* Hero Section */}
      <section className="bg-[#1F2A44] text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-20">
          {/* Back Link */}
          <Link 
            to="/resources" 
            className="inline-flex items-center text-[#C2A46D] hover:text-white transition-colors mb-8"
            data-testid="back-to-resources"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Resources
          </Link>
          
          {/* Category Badge */}
          <div className="mb-6">
            <span className="inline-block bg-[#C2A46D]/20 text-[#C2A46D] px-3 py-1 text-xs font-medium uppercase tracking-wider">
              {post.category}
            </span>
          </div>
          
          {/* Title */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif leading-tight mb-6" data-testid="post-title">
            {post.title}
          </h1>
          
          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-300">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {formatDate(post.created_at)}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {estimateReadTime(post.content)}
            </span>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16">
        {/* Excerpt/Lead */}
        <p className="text-xl text-[#555555] leading-relaxed mb-8 pb-8 border-b border-[#E8E4DC]">
          {post.excerpt}
        </p>
        
        {/* Main Content */}
        <div className="prose prose-lg max-w-none" data-testid="post-content">
          {renderContent(post.content)}
        </div>

        {/* Share Section */}
        <div className="mt-12 pt-8 border-t border-[#E8E4DC]">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <span className="text-sm text-[#555555]">Share this article:</span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleShare}
                className="border-[#E8E4DC] text-[#555555] hover:bg-[#F5F3EF]"
                data-testid="share-button"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2 text-green-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </>
                )}
              </Button>
            </div>
            
            <Link to="/book">
              <Button className="bg-[#C2A46D] hover:bg-[#B39660] text-[#1F2A44] font-medium">
                Book a Consultation
              </Button>
            </Link>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="bg-[#F5F3EF] border-t border-[#E8E4DC]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
            <h2 className="text-2xl font-serif text-[#222222] mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/resources/${relatedPost.slug}`}
                  className="group bg-white border border-[#E8E4DC] hover:border-[#1F2A44]/30 hover:shadow-lg transition-all duration-300"
                  data-testid={`related-post-${relatedPost.slug}`}
                >
                  <div className="p-6">
                    <span className="text-xs text-[#C2A46D] uppercase tracking-wider font-medium">
                      {relatedPost.category}
                    </span>
                    <h3 className="text-lg font-serif text-[#222222] mt-2 mb-3 group-hover:text-[#1F2A44] transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-sm text-[#555555] line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="bg-[#1F2A44] text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20 text-center">
          <h2 className="text-3xl md:text-4xl font-serif mb-6">
            Ready to Discuss Your Legal Needs?
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            Every situation is unique. Schedule a consultation with Attorney Brantley to get personalized guidance for your specific circumstances.
          </p>
          <Link to="/book">
            <Button className="bg-[#C2A46D] hover:bg-[#B39660] text-[#1F2A44] px-10 py-4 text-sm font-medium tracking-wide uppercase">
              Book Your Consultation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogPostPage;
